import React, { useEffect, useMemo } from 'react';

import { Header } from '@components/layout/header/Header';
import { classNames } from '../../../utils/classNames';
import {
    SideBarStates,
    useSidebarDispatch,
    useSidebarState,
} from '@components/context/SidebarContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCalendarDays, faCommentsAlt } from '@fortawesome/pro-regular-svg-icons';
import {
    faBell as faBellSolid,
    faCalendarDays as faCalendarDaysSolid,
    faCommentsAlt as faCommentsSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { useConversationsData } from '@hooks/useConversationsData';
import { Tooltip } from '@skipper/ui';
import useIsMobile from '@hooks/useIsMobile';
import { useCommentedItemsData } from '@hooks/useCommentsData';
import { CommentIconSVG } from '@components/common/svgs/CommentIconSVG';
import { useNotificationsQuery } from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';

const ChatIcon = ({ active }: { active: boolean }) => {
    const { unreadChatsCount } = useConversationsData();
    const className = active ? 'bg-teal-200 text-teal-600' : 'text-white bg-teal-600';
    return (
        <div className="relative">
            <FontAwesomeIcon icon={active ? faCommentsSolid : faCommentsAlt} className="w-6 h-6" />
            {unreadChatsCount > 0 && (
                <div
                    className={`absolute w-5 h-5 rounded-full top-[-0.5rem] right-[-0.5rem]  text-xs text-center justify-center flex items-center ${className}`}>
                    {unreadChatsCount <= 99 ? unreadChatsCount : '99'}
                </div>
            )}
        </div>
    );
};

const CommentIcon = ({ active }: { active: boolean }) => {
    const commentedItems = useCommentedItemsData();
    const unreadCommentsCount = useMemo(() => {
        if (commentedItems.all)
            return commentedItems.watching
                .concat(commentedItems.received)
                .filter((item) => item.item.comments.find((c) => !c.isViewed)).length;
    }, [commentedItems.all]);
    const className = active ? 'bg-teal-200 text-teal-600' : 'text-white bg-teal-600';
    const [hovered, setHovered] = React.useState(false);
    return (
        <div
            className="relative"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}>
            <CommentIconSVG className="scale-[0.8]" active={active || hovered} />
            {unreadCommentsCount > 0 && (
                <div
                    className={`absolute w-5 h-5 rounded-full top-[-0.5rem] right-[-0.5rem]  text-xs text-center justify-center flex items-center ${className}`}>
                    {unreadCommentsCount <= 99 ? unreadCommentsCount : '99'}
                </div>
            )}
        </div>
    );
};

const NotificationIcon = ({ active }: { active: boolean }) => {
    const [currentUser] = useUserSession();
    const invalidateQueries = useInvalidateQueries();
    const { data, refetch } = useNotificationsQuery(getClient('notifications'));
    const unreadNotificationsCount = data?.notifications?.filter((n) => !n.isViewed).length;
    const className = active ? 'bg-teal-200 text-teal-600' : 'text-white bg-teal-600';

    useEffect(() => {
        invalidateQueries(['Notifications']);
    }, [currentUser?.id]);

    return (
        <div className="relative">
            <FontAwesomeIcon icon={active ? faBellSolid : faBell} className="w-6 h-6" />
            {unreadNotificationsCount > 0 && (
                <div
                    className={`absolute w-5 h-5 rounded-full top-[-0.5rem] right-[-0.5rem]  text-xs text-center justify-center flex items-center ${className}`}>
                    {unreadNotificationsCount <= 99 ? unreadNotificationsCount : '99'}
                </div>
            )}
        </div>
    );
};

const sidebarNavigation = [
    {
        name: 'Notifications',
        panelName: SideBarStates.Notifications,
        Icon: <NotificationIcon active={false} />,
        activeIcon: <NotificationIcon active />,
    },
    // {
    //     name: 'Grades',
    //     panelName: SideBarStates.Grades,
    //     Icon: <FontAwesomeIcon icon={faAward} size="lg" />,
    // },
    {
        name: 'Comments',
        panelName: SideBarStates.Comments,
        Icon: <CommentIcon active={false} />,
        activeIcon: <CommentIcon active />,
    },
    {
        name: 'Chat',
        panelName: SideBarStates.Chat,
        Icon: <ChatIcon active={false} />,
        activeIcon: <ChatIcon active />,
    },
    // {
    //     name: 'RSSFeed',
    //     panelName: SideBarStates.RSSFeed,
    //     Icon: <FontAwesomeIcon icon={faRss} size="lg" />,
    // },
    {
        name: 'Agenda',
        panelName: SideBarStates.Agenda,
        Icon: <FontAwesomeIcon icon={faCalendarDays} className="w-5 h-5" />,
        activeIcon: <FontAwesomeIcon icon={faCalendarDaysSolid} className="w-5 h-5" />,
    },
];

const userNavigation = [
    { name: 'Your Profile', href: '#' },
    { name: 'Sign out', href: '#' },
];

export function Layout({
    children,
    showHeader = true,
    showSidebar = true,
}: {
    children: React.ReactNode;
    showHeader?: boolean;
    showSidebar?: boolean;
}) {
    return (
        <div className="h-screen bg-gray-100 flex flex-col">
            {/* Top nav*/}
            {!!showHeader && <Header />}
            {/* Bottom section */}
            <div className={`relative min-h-0 flex-1 flex ${showHeader ? 'pr-14' : ''}`}>
                {/* Main area */}
                {children}
                {/** Side Bar */}
                {!!showSidebar && <SidebarComponent />}
            </div>
        </div>
    );
}

function SidebarComponent() {
    const { toggle } = useSidebarDispatch();
    const { state } = useSidebarState();

    const isMobile = useIsMobile();

    return (
        <nav
            aria-label="Sidebar"
            className={`absolute z-[498] right-0 top-0 bottom-0 border-l-2 w-14 min-w-14 min-w-max border-gray-200 sm:bg-gray-800 sm:overflow-y-auto bg-white h-full`}>
            <div
                className={`w-14 w-full min-w-max h-full flex flex-col py-1 space-y-1 bg-white border-l border-gray-300 items-center`}>
                {sidebarNavigation.map((item) => (
                    <Tooltip key={item.name}>
                        <Tooltip.Trigger>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggle(item.panelName);
                                }}
                                key={item.name}
                                className={classNames(
                                    state === item.panelName
                                        ? 'text-primary'
                                        : 'text-gray-500 hover:text-primary',
                                    'flex-shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-lg',
                                )}>
                                <span className="sr-only">{item.name}</span>
                                {state === item.panelName ? item.activeIcon : item.Icon}
                            </button>
                        </Tooltip.Trigger>
                        <Tooltip.Content side="left" sideOffset={-8}>
                            <Tooltip.Arrow />
                            <div className="bg-gray-900 px-2 py-1 text-white rounded-sm text-sm font-larsseit font-[500] h-[28px] whitespace-pre-wrap">
                                {item.name}
                            </div>
                        </Tooltip.Content>
                    </Tooltip>
                ))}
            </div>
        </nav>
    );
}
